import React, { FC, KeyboardEvent, useMemo } from 'react';

import styled from 'styled-components';

import { UseFormRegister } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import UIFormItem, { EUIFormItemVariant } from '@aquariuxfintech/aqx-trader-react-ui/form';
import UIInput, { EUIInputVariant } from '@aquariuxfintech/aqx-trader-react-ui/input';

import { EDataTestId } from '@aqx-core/common/data-test-id/types';
import { ISignUpBasicValues } from '@aqx-core/common/hooks/form/use-sign-up-crm-form';
import {
  hasLowercase,
  hasNumber,
  hasSpecialCharacters,
  hasUppercase,
  isValidPasswordLength
} from '@aqx-core/common/validators';

import PasswordCriteriaItem from './criteria-item';

interface IAuthPasswordFieldProps {
  label: string;
  password: string;
  error?: string;
  register: UseFormRegister<ISignUpBasicValues>;
  handleKeyDown: (event: KeyboardEvent) => void;
  minLength: number;
  maxLength: number;
}

const AuthPasswordField: FC<IAuthPasswordFieldProps> = ({
  label,
  password,
  error,
  register,
  handleKeyDown,
  minLength,
  maxLength
}) => {
  const { t } = useTranslation();

  const isPasswordLengthValid = useMemo(
    () => isValidPasswordLength(password, minLength, maxLength),
    [password, minLength, maxLength]
  );
  const isLowercaseValid = useMemo(() => hasLowercase(password), [password]);
  const isUppercaseValid = useMemo(() => hasUppercase(password), [password]);
  const isNumberValid = useMemo(() => hasNumber(password), [password]);
  const isSpecialCharacterValid = useMemo(() => hasSpecialCharacters(password), [password]);

  return (
    <UIFormItem variant={EUIFormItemVariant.ITEM_WITH_FIELD_LABEL} label={label} size="large">
      <UIInput
        {...register('password')}
        variant={EUIInputVariant.BORDERED_PASSWORD}
        placeholder={t('Minimum {{minLength}} characters', { minLength })}
        value={password}
        error={error}
        defaultShowPassword={false}
        onKeyDown={handleKeyDown}
        dataTestId={EDataTestId.SIGN_UP_PASSWORD_INPUT}
        size="large"
      />
      <StyledCriteriaContainer>
        <PasswordCriteriaItem isValid={isPasswordLengthValid}>
          {t('{{minLength}}-{{maxLength}} characters', { minLength, maxLength })}
        </PasswordCriteriaItem>
        <PasswordCriteriaItem isValid={isUppercaseValid}>{t('1 upper case letter')}</PasswordCriteriaItem>
        <PasswordCriteriaItem isValid={isLowercaseValid}>{t('1 lower case letter')}</PasswordCriteriaItem>
        <PasswordCriteriaItem isValid={isNumberValid}>{t('1 or more numbers')}</PasswordCriteriaItem>
        <PasswordCriteriaItem isValid={isSpecialCharacterValid}>
          {t('1 or more special characters')}
        </PasswordCriteriaItem>
      </StyledCriteriaContainer>
    </UIFormItem>
  );
};

export default AuthPasswordField;

const StyledCriteriaContainer = styled.div`
  margin-top: 12px;
`;
